import type { MenuItem } from "@shared/contracts";
import type { MenuRevision, MenuRevisionInput, MenuSyncStatusResponse, PublishedMenu } from "@shared/menu";

async function requestJson<T>(url: string, init: RequestInit | undefined, fetcher: typeof fetch): Promise<T | null> {
  const response = await fetcher.call(globalThis, url, { ...init, credentials: "same-origin" });
  if (response.status === 401 || response.status === 403 || response.status === 404 || response.status === 503) return null;
  if (!response.ok) throw new Error(`Menu API request failed (${response.status})`);
  return (await response.json()) as T;
}

export async function fetchPublishedMenu(fetcher: typeof fetch = fetch): Promise<PublishedMenu | null> {
  return requestJson<PublishedMenu>("/api/menu", undefined, fetcher);
}

export async function fetchMenuRevisions(fetcher: typeof fetch = fetch): Promise<MenuRevision[] | null> {
  const result = await requestJson<{ revisions: MenuRevision[] }>("/api/admin/menu/revisions", undefined, fetcher);
  return result?.revisions ?? null;
}

export async function fetchMenuSyncStatus(fetcher: typeof fetch = fetch): Promise<MenuSyncStatusResponse | null> {
  return requestJson<MenuSyncStatusResponse>("/api/admin/menu/sync-status", undefined, fetcher);
}

export async function createMenuDraft(input: MenuRevisionInput | MenuItem[], fetcher: typeof fetch = fetch): Promise<MenuRevision | null> {
  const payload = Array.isArray(input) ? { items: input } : input;
  const result = await requestJson<{ revision: MenuRevision }>("/api/admin/menu/revisions", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(payload) }, fetcher);
  return result?.revision ?? null;
}

export async function publishMenuRevision(revisionId: string, fetcher: typeof fetch = fetch): Promise<PublishedMenu | null> {
  return requestJson<PublishedMenu>(`/api/admin/menu/revisions/${encodeURIComponent(revisionId)}/publish`, { method: "POST" }, fetcher);
}

export async function rollbackMenuRevision(revisionId: string, fetcher: typeof fetch = fetch): Promise<PublishedMenu | null> {
  return requestJson<PublishedMenu>(`/api/admin/menu/revisions/${encodeURIComponent(revisionId)}/rollback`, { method: "POST" }, fetcher);
}
